import { getSpaceName } from "@app/lib/spaces";
import type { SpaceCategoryInfo } from "@app/types/api/spaces";
import type { SpaceType } from "@app/types/space";
import {
  Button,
  ContentMessage,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Spinner,
  Trash01,
} from "@dust-tt/sparkle";
import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

interface ConfirmDeleteSpaceDialogProps {
  space: SpaceType;
  handleDelete: () => void;
  spaceInfoByCategory: { [key: string]: SpaceCategoryInfo } | undefined;
  isDeleting: boolean;
}

export function ConfirmDeleteSpaceDialog({
  space,
  handleDelete,
  spaceInfoByCategory,
  isDeleting,
}: ConfirmDeleteSpaceDialogProps) {
  const { t } = useLingui();
  const uniqueAgentNames = spaceInfoByCategory
    ? [
        ...new Set(
          Object.values(spaceInfoByCategory)
            .flatMap((category) => category.usage.agents)
            .map((agent) => agent.name)
            .filter((name) => name && name.length > 0)
        ),
      ]
    : [];
  const uniqueSkillNames = spaceInfoByCategory
    ? [
        ...new Set(
          Object.values(spaceInfoByCategory)
            .flatMap((category) => category.usage.skills)
            .map((skill) => skill.name)
            .filter((name) => name && name.length > 0)
        ),
      ]
    : [];

  const spaceName = `${getSpaceName(space)}`;
  const hasAgents = uniqueAgentNames.length > 0;
  const hasSkills = uniqueSkillNames.length > 0;
  const agentCount = uniqueAgentNames.length;
  const skillCount = uniqueSkillNames.length;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="flex w-full flex-col items-end">
          <Button
            icon={Trash01}
            size="xs"
            variant="warning"
            label={t`Delete domain`}
          />
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t`Deleting ${spaceName}`}</DialogTitle>
        </DialogHeader>
        {isDeleting ? (
          <div className="flex justify-center py-8">
            <Spinner variant="dark" size="md" />
          </div>
        ) : (
          <>
            <DialogContainer className="space-y-4">
              {hasAgents && (
                <ContentMessage
                  variant="warning"
                  // TODO: change to show names of public agents and then number of unpublished agents
                  title={t`${plural(agentCount, {
                    one: "# agent uses tools that depend on this domain and will be impacted by its deletion",
                    other:
                      "# agents use tools that depend on this domain and will be impacted by its deletion",
                  })}`}
                />
              )}
              {hasSkills && (
                <ContentMessage
                  variant="warning"
                  title={t`${plural(skillCount, {
                    one: "# skill depends on this domain and will be impacted by its deletion",
                    other:
                      "# skills depend on this domain and will be impacted by its deletion",
                  })}`}
                />
              )}
              <div>
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    Are you sure you want to permanently delete domain{" "}
                    {spaceName}? This action cannot be undone.
                  </Trans>
                </p>
              </div>
            </DialogContainer>
            <DialogFooter
              leftButtonProps={{
                label: t`Cancel`,
                variant: "outline",
              }}
              rightButtonProps={{
                label: t`Delete`,
                variant: "warning",
                onClick: async () => {
                  void handleDelete();
                },
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
